import React, { useContext } from "react";
import { observer } from "mobx-react";
import { useLocation, match } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Location } from "history";
import { RootAppStoreContext } from "@state/context/root-store/RootAppStoreContext";
import { isLoginFlow } from "@utils/membership/membershipUtils";
import { DefaultPath } from "@utils/common/defaultRoutePathsEnum";
import { WrappedNavLink } from "@components/navigation";
import { MainHtmlElementContext } from "@state/context/main-html/MainHtmlElementContext";
import SpecialFooterOption from "./components/SpecialFooterOption";
import { useAppContext } from "@state/hooks/shared";
import useSafeAreaListener from "@utils/specific/capacitor/useSafeAreaListener";
import { isCapacitorPlatform } from "@utils/specific/capacitor/helpers";

enum SpecialFooterOptionEnum {
	GAMES,
	CASINO,
	LIVE_CASINO,
	DEFAULT,
}

function Footer() {
	const { culture } = useAppContext();
	const { pathname } = useLocation();
	const { userAuthStore } = useContext(RootAppStoreContext);
	const { bottom } = useSafeAreaListener();

	const { t } = useTranslation();

	const mainElRef = React.useContext(MainHtmlElementContext);

	const getSpecialFooterOption = () => {
		if (
			WEBPACK_IS_CASINO_ENABLED &&
			(!userAuthStore.isLoggedIn || userAuthStore.isInternetUser)
		) {
			return SpecialFooterOptionEnum.CASINO;
		}
		if (
			WEBPACK_IS_CASINO_LIVE_ENABLED &&
			(!userAuthStore.isLoggedIn || userAuthStore.isInternetUser)
		) {
			return SpecialFooterOptionEnum.LIVE_CASINO;
		}
		if (WEBPACK_IS_GAMES_ENABLED) {
			return SpecialFooterOptionEnum.GAMES;
		} else {
			return SpecialFooterOptionEnum.DEFAULT;
		}
	};

	if (isLoginFlow(pathname)) {
		return null;
	}

	return (
		<footer className="l--footer l__footer">
			{isCapacitorPlatform() && (
				<div
					className="l__footer__overlay"
					style={{ height: `${bottom}px` }}
				></div>
			)}

			<nav className="nav--primary">
				<ul className="nav--primary__menu">
					<li className="nav--primary__item">
						<WrappedNavLink
							to={`/${culture}/${DefaultPath.HOME}`}
							className="nav--primary__link"
							activeClassName="is-active"
							isActive={(
								match: match | null,
								location: Location<unknown>
							): boolean => location.pathname.includes(`/home`)}
						>
							<span className="nav--primary__icon">
								<i className="nav--primary__icon__inactive u-icon u-icon--base u-icon--home--reg" />
								<i className="nav--primary__icon__active u-icon u-icon--base u-icon--home--str u-color--brand--primary" />
							</span>

							<span className="nav--primary__title">
								{t<string>("FOOTER.HOME")}
							</span>
						</WrappedNavLink>
					</li>

					<li className="nav--primary__item">
						<WrappedNavLink
							to={`/${culture}/sports/full/land`}
							className="nav--primary__link"
							activeClassName="is-active"
							isActive={(_, location) => {
								return (
									location.pathname.startsWith(
										`/${culture}/sports`
									) && !location.pathname.includes("event/")
								);
							}}
						>
							<span className="nav--primary__icon">
								<i className="nav--primary__icon__inactive u-icon u-icon--base u-icon--sports-betting" />
								<i className="nav--primary__icon__active u-icon u-icon--base u-icon--sports-betting--str u-color--brand--primary" />
							</span>

							<span className="nav--primary__title">
								{t<string>("FOOTER.SPORTS")}
							</span>
						</WrappedNavLink>
					</li>

					<li className="nav--primary__item">
						<WrappedNavLink
							onClick={() => {
								mainElRef?.scrollTo({ top: 0 });
							}}
							to={`/${culture}/live`}
							className="nav--primary__link"
							activeClassName="is-active"
							isActive={(
								match: match | null,
								location: Location<unknown>
							): boolean => {
								if (location.pathname == `/${culture}/live`) {
									return true;
								} else if (match) {
									return true;
								}

								return false;
							}}
						>
							<span className="nav--primary__icon">
								<i className="nav--primary__icon__inactive u-icon u-icon--base u-icon--live--reg" />
								<i className="nav--primary__icon__active u-icon u-icon--base u-icon--live--str u-color--brand--primary" />
							</span>

							<span className="nav--primary__title">
								{t<string>("FOOTER.LIVE")}
							</span>
						</WrappedNavLink>
					</li>

					<li className="nav--primary__item">
						<WrappedNavLink
							to={`/${culture}/my-bets`}
							className="nav--primary__link"
							activeClassName="is-active"
							isActive={(
								match: match | null,
								location: Location<unknown>
							): boolean => {
								if (
									location.pathname == `/${culture}/my-bets`
								) {
									return true;
								} else if (match) {
									return true;
								}
								return false;
							}}
						>
							<span className="nav--primary__icon">
								<i className="nav--primary__icon__inactive u-icon u-icon--base u-icon--bets--reg" />
								<i className="nav--primary__icon__active u-icon u-icon--base u-icon--bets--str u-color--brand--primary" />
							</span>

							<span className="nav--primary__title">
								{t<string>("FOOTER.MY_BETS")}
							</span>
						</WrappedNavLink>
					</li>

					<SpecialFooterOption
						option={getSpecialFooterOption()}
					/>
				</ul>
			</nav>
		</footer>
	);
}

export default observer(Footer);
